import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { t } from 'i18next';
import { subMonths } from 'date-fns';
import useChatRoomMessages from '../../components/api/queries/useCharRoomMessages';
import { useOptimisticChatMessages } from './hooks/useOptimisticChatMessages';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { queryGet } from '@pocapan/shared/utils/queryClient';
import { attachJosa, getGroupFlags } from '@pocapan/shared/utils/bettieUtils';
import BettieMessageInput from './components/BettieMessageInput';
import StickyHeader from '../components/bar/StickyHeader';
import MessageBubble from './components/MessageBubble';
import ProfileAvatar from '../my-page/components/ProfileAvatar';
import type { components } from '@pocapan/shared/types/schema';
import { useFeatureEnabled } from '@pocapan/shared/domains/features/ui/hooks/useFeatureEnabled';
import { FEATURE_KEYS } from '@pocapan/shared/domains/features/constants';
import { More, Xmark } from '@peacecover/icon';
import BettieChatRoomSettingBottomDrawer from '../../components/drawer/BettieChatRoomSettingBottomDrawer';
import { useGetUserPreferences } from '@pocapan/shared/domains/user/ui/hooks/useGetUserPreferences';
import PopUpBasic from '../components/popup/PopUpBasic';
import leaveChatRoom from '../../components/api/mutations/legacy/leaveChatRoom';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import BettieNicknameUpdateBottomDrawer from '../../components/drawer/BettieNicknameUpdateBottomDrawer';
import {
  deleteBettieChatMessage,
  resendBettieChatMessage
} from '@pocapan/shared/domains/bettie/services/bettieService';
import { ReactComponent as LikeIcon } from '../..//icons/likeGradientShape.svg';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import { useSequentialBettieReplies } from './hooks/useSequentialBettieReplies';
import { useGetFanLetters } from '@pocapan/shared/domains/bettie/ui/hooks/useGetFanLetters';
import { RoundBadge } from '@peacecover/component';
import EmptyHeartCard from './components/EmptyHeartCard';
import saveData from '@pocapan/shared/utils/saveData';
import BettieProfileUpdateBottomDrawer from '../../components/drawer/BettieUpdateBottomDrawer';
import BettieMissedNotePopup from './components/BettieMissedNotePopup';
import { useActOnBettieProactiveMessage } from '@pocapan/shared/domains/bettie/ui/hooks/useActOnBettieProactiveMessage';
import {
  createBettieMessage,
  getBettieProactiveMessages,
  type GetBettieProactiveMessagesResponse
} from '@pocapan/shared/domains/bettie/services/bettieService';
import { useAdMob } from './hooks/useAdMob';
import { useWarningToast } from '../hooks/useWarningToast';

type BettieMessage = components['schemas']['ChatRoomMessage'];
type ChatRoomMessagesPage = { chatRoomMessages?: BettieMessage[] };
type BettieChatRoom = components['schemas']['ChatRoom'];
type BettieProactiveMessage = GetBettieProactiveMessagesResponse['proactiveMessages'][number];

const BettieChatRoomPage = () => {
  const { chatRoomId } = useParams<{ chatRoomId: string }>();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLeaveChatRoomPopupOpen, setIsLeaveChatRoomPopupOpen] = useState(false);
  const [isProfileUpdateSheetOpen, setIsProfileUpdateSheetOpen] = useState(false);
  const [isNicknameUpdateSheetOpen, setIsNicknameUpdateSheetOpen] = useState(false);
  const [isFailedMessagePopupOpen, setIsFailedMessagePopupOpen] = useState(false);
  const [isChargeBottomSheetOpen, setIsChargeBottomSheetOpen] = useState(false);
  const [failedMessage, setFailedMessage] = useState({
    id: '',
    content: ''
  });
  const [freeChatCount, setFreeChatCount] = useState(0);
  const [isLetterBannerOpen, setIsLetterBannerOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 초기 스크롤이 완료되었는지 확인하는 Ref
  const hasScrolledInitially = useRef(false);

  // 베타 유저 확인
  const { data: isBettieBetaUser } = useFeatureEnabled(FEATURE_KEYS.BETTIE_BETA_USER);
  const isBettieBetaUserEnabled = isBettieBetaUser?.featureFlag.enabled;

  //
  const { data: isBettieProfileUpdateAvailable } = useFeatureEnabled(
    FEATURE_KEYS.BETTIE_PROFILE_CHANGE
  );
  const isBettieProfileUpdateAvailableEnabled = isBettieProfileUpdateAvailable?.featureFlag.enabled;

  const { data: userPreferences } = useGetUserPreferences({ enabled: true });
  const isBettieChatAlarm = userPreferences?.userPreferences.alert?.bettieChat ?? true; // null일 경우 알림이 켜져있는 것으로 간주

  const { data: chatRoom, isLoading: isChatRoomLoading } = useQuery(
    ['chatRoom', chatRoomId],
    () => queryGet(`/api/v1/chat_rooms/${chatRoomId}`),
    {
      enabled: !!chatRoomId,
      select: (r: { chatRoom: BettieChatRoom }) => r.chatRoom,
      cacheTime: 0
    }
  );

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } = useChatRoomMessages(
    chatRoomId,
    50
  );

  const { data: fanLetters } = useGetFanLetters(
    { only_unseen: true, chat_room_id: chatRoomId },
    { enabled: !!chatRoomId, refetchOnMount: true, cacheTime: 0, staleTime: 0 }
  );
  const firstFanLetterId = fanLetters?.[0]?.fanLetterId ?? '';
  const actOnProactiveMessage = useActOnBettieProactiveMessage();
  const showWarningToast = useWarningToast();
  const [isMissedNoteReplying, setIsMissedNoteReplying] = useState(false);
  const [activeProactiveMessage, setActiveProactiveMessage] = useState<
    BettieProactiveMessage | undefined
  >();
  const pendingMissedNoteReplyRef = useRef<string | null>(null);
  const hasEarnedMissedNoteReplyRewardRef = useRef(false);
  const hasRequestedProactiveMessagesRef = useRef(false);

  const messages: BettieMessage[] = useMemo(
    () =>
      (data?.pages as ChatRoomMessagesPage[] | undefined)
        ?.flatMap((page) => page.chatRoomMessages ?? [])
        .reverse() || [],
    [data]
  );

  const { displayMessages, onBeforeSend, onAfterSent, onSendFailed, lastKey } =
    useOptimisticChatMessages({
      baseMessages: messages,
      chatRoom,
      chatRoomId
    });

  // 스크롤 이벤트 처리
  useInfiniteScroll({
    fetchNextPage,
    isFetching,
    hasNextPage: hasNextPage ?? false,
    data: messages
  });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const { renderMessages, startSequentialReveal } = useSequentialBettieReplies({
    baseMessages: displayMessages,
    scrollToBottom
  });

  // 최초 진입 시점에만 스크롤을 맨 아래로 내림
  useEffect(() => {
    const isInitialLoadCompleted = !isChatRoomLoading && messages.length > 0;

    // 아직 초기 스크롤을 하지 않은 경우에만 실행
    if (isInitialLoadCompleted && !hasScrolledInitially.current) {
      // DOM 렌더링이 완료된 후 스크롤을 보장하기 위해 setTimeout을 사용
      setTimeout(() => {
        scrollToBottom('auto');
        hasScrolledInitially.current = true;
      }, 0);
    }
  }, [isChatRoomLoading, messages.length, scrollToBottom]);

  // 마지막 메시지가 변경되었을 때 스크롤 위치 복원
  useEffect(() => {
    if (!lastKey) return;
    scrollToBottom('smooth');
  }, [lastKey, scrollToBottom]);

  useEffect(() => {
    if (chatRoom) {
      setFreeChatCount(chatRoom.bettieChatsCount);
    }
  }, [chatRoom]);

  useEffect(() => {
    return () => {
      saveData('hasSeenStopChatBubble', 'true');
    };
  }, []);

  useEffect(() => {
    if (!chatRoomId) return;

    let isMounted = true;

    hasRequestedProactiveMessagesRef.current = false;
    setActiveProactiveMessage(undefined);

    async function fetchProactiveMessagesOnce() {
      if (!chatRoomId || hasRequestedProactiveMessagesRef.current) return;

      hasRequestedProactiveMessagesRef.current = true;

      try {
        const data = await getBettieProactiveMessages(chatRoomId);

        if (isMounted) {
          setActiveProactiveMessage(data.proactiveMessages?.[0]);
        }
      } catch (_error) {
        // 실패하면 부재중 메시지 팝업 없이 채팅방 진입을 계속한다.
      }
    }

    fetchProactiveMessagesOnce();

    return () => {
      isMounted = false;
    };
  }, [chatRoomId]);

  const handleLeaveChatRoom = async () => {
    const response = await leaveChatRoom(chatRoomId);
    if (response.success) {
      setIsLeaveChatRoomPopupOpen(false);
      queryClient.invalidateQueries(['chatRooms']);
      queryClient.invalidateQueries(['bettieBalances']);
      navigate('/home', { replace: true });
    }
  };

  const handleResendMessage = async (chatRoomMessageId: string, content: string) => {
    setIsFailedMessagePopupOpen(false);
    // 낙관적 업데이트 처리
    onBeforeSend(content);
    try {
      const data = await resendBettieChatMessage(chatRoomMessageId);
      if (data?.userMessage) {
        await refetch();
        onAfterSent();
      }
      if (data.meta.bettieChatsCount !== undefined && data.meta.bettieChatsCount !== null) {
        setFreeChatCount((prev) => Math.min(prev, data.meta.bettieChatsCount as number));
      }
    } catch (error) {
      onSendFailed();
      console.error(error);
    }
  };

  const handleDeleteMessage = async (chatRoomMessageId: string) => {
    try {
      await deleteBettieChatMessage(chatRoomMessageId);
      refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setIsFailedMessagePopupOpen(false);
    }
  };

  const hasMoreFreeChat = freeChatCount > 0;
  const showStopButton = Boolean(displayMessages[displayMessages.length - 1]?.isMine === false);
  const isMissedNotePopupVisible = Boolean(activeProactiveMessage);

  const hideActiveProactiveMessage = useCallback(() => {
    setActiveProactiveMessage(undefined);
  }, []);

  const submitMissedNoteReply = useCallback(
    async (content: string) => {
      if (!chatRoomId || !content.trim()) return;

      const data = await createBettieMessage({
        message: {
          chat_room_id: chatRoomId,
          content: content.trim(),
          message_type: 'text',
          sent_at: new Date().toISOString()
        }
      });

      await refetch();
      hideActiveProactiveMessage();
      onAfterSent();

      const replyIds =
        (data.bettieMessages || [])
          .map((message) => message?.chatRoomMessageId)
          .filter((value): value is string => Boolean(value)) ?? [];

      if (replyIds.length > 0) {
        const delays: Record<string, number> = {};
        for (const id of replyIds) {
          delays[id] = 2000;
        }
        startSequentialReveal(replyIds, delays);
      }

      if (data.meta.bettieChatsCount !== undefined && data.meta.bettieChatsCount !== null) {
        setFreeChatCount((prev) => Math.min(prev, data.meta.bettieChatsCount as number));
      }
    },
    [chatRoomId, hideActiveProactiveMessage, onAfterSent, refetch, startSequentialReveal]
  );

  const { showAd: showMissedNoteReplyAd } = useAdMob({
    autoLoad: true,
    adType: 'rewardedInterstitial',
    featureKind: 'ads',
    onReward: () => {
      hasEarnedMissedNoteReplyRewardRef.current = true;
    },
    onAdClosed: () => {
      const pendingReply = pendingMissedNoteReplyRef.current;
      const hasReward = hasEarnedMissedNoteReplyRewardRef.current;

      pendingMissedNoteReplyRef.current = null;
      hasEarnedMissedNoteReplyRewardRef.current = false;

      if (!pendingReply || !hasReward) {
        setIsMissedNoteReplying(false);
        return;
      }

      submitMissedNoteReply(pendingReply)
        .catch(() => {
          showWarningToast('bottom', '답장을 보내지 못했어요. 다시 시도해 주세요.');
        })
        .finally(() => {
          setIsMissedNoteReplying(false);
        });
    },
    onError: () => {
      pendingMissedNoteReplyRef.current = null;
      hasEarnedMissedNoteReplyRewardRef.current = false;
      setIsMissedNoteReplying(false);
      showWarningToast('bottom', '광고를 불러오지 못했어요. 다시 시도해 주세요.');
    }
  });

  const handleReplyFromMissedNotePopup = async (content: string) => {
    const trimmedContent = content.trim();
    if (!chatRoomId || !trimmedContent) return;

    if (!hasMoreFreeChat) {
      pendingMissedNoteReplyRef.current = trimmedContent;
      hasEarnedMissedNoteReplyRewardRef.current = false;
      setIsMissedNoteReplying(true);
      showMissedNoteReplyAd();
      return;
    }

    setIsMissedNoteReplying(true);
    try {
      await submitMissedNoteReply(trimmedContent);
    } catch (_error) {
      showWarningToast('bottom', '답장을 보내지 못했어요. 다시 시도해 주세요.');
    } finally {
      setIsMissedNoteReplying(false);
    }
  };

  if (isChatRoomLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  if (!chatRoom) {
    return null;
  }

  const handleMissedNoteAction = async (type: 'later' | 'dislike') => {
    if (!chatRoomId || !activeProactiveMessage) return;

    try {
      await actOnProactiveMessage.mutateAsync({
        chatRoomId,
        proactiveMessageHistoryId: activeProactiveMessage.proactiveMessageHistoryId,
        body: {
          action: {
            type
          }
        }
      });
      hideActiveProactiveMessage();
    } catch (error) {
      console.error(error);
    }
  };

  const handleBackButtonClick = () => {
    const from = (location.state as { from?: string } | null)?.from;
    const backPath = from === 'bettie-chat-list' ? '/bettie-chat-list' : '/home';

    // 메시지가 20개 이상이고, lastFeedbackAt이 없거나 한달 전일 때만 state 전달
    const oneMonthAgo = subMonths(new Date(), 1);
    const shouldShowSatisfaction =
      displayMessages.length >= 20 &&
      (!chatRoom?.lastFeedbackAt || new Date(chatRoom.lastFeedbackAt) < oneMonthAgo);

    // BettieChatListPage에서는 만족도 미지원. navigate(path) push 시 히스토리 중복으로 무한 반복 발생
    if (from === 'bettie-chat-list') {
      navigate(-1);
      return;
    }

    // ChatMainPage에서 만족도 바텀시트를 열기 위해 replace로 이동 (push 시 무한 반복 발생)
    if (shouldShowSatisfaction) {
      navigate(backPath, {
        replace: true,
        state: {
          title: 'bettieSatisfaction',
          thumbnailUrl: chatRoom.bettieProfile.pictureUrl,
          nickname: chatRoom.myNickname,
          chatRoomId: chatRoomId,
          from
        }
      });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="bg-background-light dark:bg-background-dark">
      <StickyHeader
        title={chatRoom.bettieProfile.nickname}
        showBackButton={true}
        showTitle={true}
        alwaysShowTitle={true}
        rightContent={
          <div className="flex items-center justify-center gap-[10px]">
            <button
              type="button"
              className="flex items-center justify-center gap-[2px] whitespace-nowrap"
              onClick={() => setIsChargeBottomSheetOpen(true)}
            >
              <LikeIcon width={24} height={24} />
              <p className="text-[16px] font-medium leading-[20px] text-text-main-lv1 whitespace-nowrap">
                {freeChatCount}
              </p>
            </button>
            <More size={24} className="text-text-main-lv1" onClick={() => setIsModalOpen(true)} />
          </div>
        }
        onClickBackButton={handleBackButtonClick}
      />
      {isMissedNotePopupVisible && (
        <BettieMissedNotePopup
          key={activeProactiveMessage?.proactiveMessageHistoryId}
          nickname={chatRoom.bettieProfile.nickname ?? ''}
          thumbnailUrl={chatRoom.bettieProfile.thumbnailUrl ?? chatRoom.bettieProfile.pictureUrl}
          proactiveMessage={activeProactiveMessage}
          isOutOfPoints={!hasMoreFreeChat}
          disabled={!isBettieBetaUserEnabled}
          isSubmitting={isMissedNoteReplying || actOnProactiveMessage.isLoading}
          onReply={handleReplyFromMissedNotePopup}
          onLater={() => handleMissedNoteAction('later')}
          onDislike={() => handleMissedNoteAction('dislike')}
        />
      )}
      {displayMessages.length > 0 ? (
        <>
          <div className="h-[35px] mt-[env(safe-area-inset-top)]" />
          <div className="pt-[16px] pl-[12px] pr-[8px] pb-[140px]">
            {!isMissedNotePopupVisible && firstFanLetterId && (
              <BettieLetterOpenBanner
                nickname={chatRoom.bettieProfile.nickname ?? ''}
                fanLetterId={firstFanLetterId}
              />
            )}
            {/* <SubscriptionUpgradeBanner /> */}
            {!isMissedNotePopupVisible && isLetterBannerOpen && (
              <BettieLetterBanner
                subjectId={chatRoom.subjectId ?? ''}
                memberName={chatRoom.bettieProfile.nickname}
                onClose={() => setIsLetterBannerOpen(false)}
              />
            )}
            {isFetching && (
              <div className="flex items-center justify-center gap-[6px] mt-[12px]">
                <GradientLoadingSpinner size={20} />
                <p className="text-[11px] text-text-main-lv2 font-medium">
                  {t('BettieChatRoom.loadingPrevious')}
                </p>
              </div>
            )}
            {renderMessages.map((m, index) => {
              const { previousDate, isFirstMessage, isFirstInGroup, isLastInGroup } = getGroupFlags(
                renderMessages,
                index
              );
              const nextMessage =
                index < renderMessages.length - 1 ? renderMessages[index + 1] : null;
              const nextDate = nextMessage?.sentAt || null;

              return (
                <div key={m.chatRoomMessageId} id={`message-${m.chatRoomMessageId}`}>
                  <MessageBubble
                    message={m}
                    previousDate={previousDate}
                    nextDate={nextDate}
                    isFirstMessage={isFirstMessage}
                    isLastInGroup={isLastInGroup}
                    isFirstInGroup={isFirstInGroup}
                    onProfileImageClick={() => {
                      if (isBettieProfileUpdateAvailableEnabled) {
                        navigate(`/bettie/${chatRoom.chatRoomId}/change-profile`);
                      } else {
                        setIsProfileUpdateSheetOpen(true);
                      }
                    }}
                    isPopupOpen={() => {
                      setFailedMessage({
                        id: m.chatRoomMessageId,
                        content: m.content
                      });
                      setIsFailedMessagePopupOpen(true);
                    }}
                  />
                </div>
              );
            })}
            <div ref={messagesEndRef} />
            {freeChatCount === 0 && (
              <EmptyHeartCard openChargeBottomSheet={() => setIsChargeBottomSheetOpen(true)} />
            )}
          </div>
        </>
      ) : (
        <EmptyBettieChatCase chatRoom={chatRoom} />
      )}

      <div className="fixed pb-[env(safe-area-inset-bottom)] bottom-0 left-0 right-0 shadow-[0_-2px_2px_0_rgba(0,0,0,0.02),0_-3px_8px_0_rgba(0,0,0,0.03)] dark:shadow-[0_-2px_2px_0_rgba(0,0,0,0.12),0_-3px_8.6px_0_rgba(0,0,0,0.28)] bg-background-light dark:bg-background-dark">
        <BettieMessageInput
          chatRoomUuid={chatRoomId || ''}
          bettieNickname={chatRoom.bettieProfile.nickname ?? ''}
          onBeforeSend={(content) => {
            onBeforeSend(content);
          }}
          onMessageSent={async () => {
            await refetch();
            onAfterSent();
          }}
          onBettieReplies={(ids) => {
            const delays: Record<string, number> = {};
            for (const id of ids) {
              delays[id] = 2000; // 2000ms 고정 지연
            }
            startSequentialReveal(ids, delays);
          }}
          onSendFailed={onSendFailed}
          disabled={!isBettieBetaUserEnabled || !hasMoreFreeChat}
          setFreeChatCount={setFreeChatCount}
          showStopButton={showStopButton}
        />
      </div>
      <BettieChatRoomSettingBottomDrawer
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isBettieChatAlarm={isBettieChatAlarm}
        chatRoom={chatRoom}
        leavePopupOpen={() => setIsLeaveChatRoomPopupOpen(true)}
        openProfileUpdateSheet={() => {
          setIsModalOpen(false);
          if (isBettieProfileUpdateAvailableEnabled) {
            navigate(`/bettie/${chatRoom.chatRoomId}/change-profile`);
          } else {
            setIsProfileUpdateSheetOpen(true);
          }
        }}
        openNicknameUpdateSheet={() => {
          setIsModalOpen(false);
          setIsNicknameUpdateSheetOpen(true);
        }}
      />
      <BettieProfileUpdateBottomDrawer
        isOpen={isProfileUpdateSheetOpen}
        onClose={() => setIsProfileUpdateSheetOpen(false)}
        bettieProfile={chatRoom.bettieProfile}
        chatRoomId={chatRoomId as string}
      />
      <BettieNicknameUpdateBottomDrawer
        isOpen={isNicknameUpdateSheetOpen}
        onClose={() => setIsNicknameUpdateSheetOpen(false)}
        bettieProfile={chatRoom.bettieProfile}
        chatRoomId={chatRoomId as string}
        currentNickname={chatRoom.myNickname ?? ''}
      />
      {isLeaveChatRoomPopupOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-[1000] flex items-center justify-center">
          <PopUpBasic
            type="short"
            title={t('BettieChatRoom.leaveChatRoomPopup.title')}
            description={t('BettieChatRoom.leaveChatRoomPopup.description')}
            primaryButtonText={t('BettieChatRoom.leaveChatRoomPopup.cancelButton')}
            onPrimaryButtonClick={() => {
              setIsLeaveChatRoomPopupOpen(false);
            }}
            secondaryButtonText={t('BettieChatRoom.leaveChatRoomPopup.leaveButton')}
            onSecondaryButtonClick={handleLeaveChatRoom}
            className="w-[311px]"
          />
        </div>
      )}
      {isFailedMessagePopupOpen && (
        <button
          type="button"
          className="fixed top-0 left-0 w-full h-full bg-black/50 z-[1000] flex items-center justify-center"
          onClick={() => setIsFailedMessagePopupOpen(false)}
          aria-label="close failed message popup"
        >
          <div role="dialog" onMouseDown={(e) => e.stopPropagation()}>
            <PopUpBasic
              type="short"
              title={t('BettieChatRoom.failedMessagePopup.title')}
              description={t('BettieChatRoom.failedMessagePopup.description')}
              primaryButtonText={t('BettieChatRoom.failedMessagePopup.delete')}
              secondaryButtonText={t('BettieChatRoom.failedMessagePopup.resend')}
              onPrimaryButtonClick={() => handleDeleteMessage(failedMessage.id)}
              onSecondaryButtonClick={() =>
                handleResendMessage(failedMessage.id, failedMessage.content)
              }
              className="w-[311px] z-[2000]"
            />
          </div>
        </button>
      )}
      {isChargeBottomSheetOpen && (
        <BettieChargeBottomSheet
          isOpen={isChargeBottomSheetOpen}
          onClose={() => setIsChargeBottomSheetOpen(false)}
        />
      )}
    </div>
  );
};

export default BettieChatRoomPage;

const EmptyBettieChatCase = ({ chatRoom }: { chatRoom: BettieChatRoom }) => {
  return (
    <div className="mt-[calc(247px+env(safe-area-inset-top))] flex flex-col items-center gap-[16px]">
      <ProfileAvatar
        thumbnailUrl={chatRoom.bettieProfile.pictureUrl}
        widthAndHeight="84px"
        svgWidthAndHeight="56px"
      />
      <div className="px-[22px] pt-[15px] pb-[16px] bg-separator1 rounded-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),0_0_12px_0_rgba(0,0,0,0.05)]">
        <p className="text-[13px] font-semibold leading-[16px] text-text-main-lv2 whitespace-pre-line break-words">
          {t('BettieChatRoom.chatWithBettie', {
            nickname: attachJosa(chatRoom.bettieProfile.nickname, '과/와')
          })}
        </p>
      </div>
    </div>
  );
};

const BettieLetterBanner = ({
  subjectId,
  memberName,
  onClose
}: {
  subjectId: string;
  memberName: string;
  onClose: () => void;
}) => {
  const isBasicPersona = subjectId === '16e2334d-9f56-4695-9bee-247c92b7b100';

  const bannerText = isBasicPersona
    ? t('BettieChatRoom.letterBanner.warningText', { nickname: memberName })
    : t('BettieChatRoom.letterBanner.message');

  return (
    <div className="fixed top-[calc(env(safe-area-inset-top)+64px)] left-0 right-0 flex items-center mx-[8px] justify-between bg-background-elevated py-[12px] pr-[15px] pl-[12px] rounded-[12px] shadow-[0_0_4px_0_rgba(0,0,0,0.05),_0_0_11.6px_0_rgba(0,0,0,0.05)] z-50">
      <div className="flex items-center gap-[8px] flex-1 min-w-0 overflow-hidden">
        {isBasicPersona ? (
          <div className="w-[24px] h-[24px] flex items-center justify-center bg-surface-lv2 rounded-full flex-shrink-0">
            <img src="/assets/icons/warning.svg" alt="" className="w-[17px] h-[14px]" />
          </div>
        ) : (
          <RoundBadge colorVariant="secondary">Tip</RoundBadge>
        )}

        <div className="flex w-full overflow-hidden mask-image-linear-gradient">
          <div className="infinite-scroll flex flex-shrink-0 gap-4 pr-4 whitespace-nowrap">
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
              {bannerText}
            </p>
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
              {bannerText}
            </p>
          </div>
          <div
            className="infinite-scroll flex flex-shrink-0 gap-4 pr-4 whitespace-nowrap"
            aria-hidden="true"
          >
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
              {bannerText}
            </p>
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
              {bannerText}
            </p>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="close letter banner"
        className="flex items-center justify-center flex-shrink-0 ml-[8px]"
      >
        <Xmark size={14} className="text-text-main-lv3" />
      </button>
    </div>
  );
};

const BettieLetterOpenBanner = ({
  nickname,
  fanLetterId
}: {
  nickname: string;
  fanLetterId: string;
}) => {
  const navigate = useNavigate();
  return (
    <div className="fixed top-[calc(env(safe-area-inset-top)+64px)] left-0 right-0 z-[100] mx-[8px]">
      <div className="relative">
        <div
          className="flex items-center justify-between bg-gradient-multi-rainbox-lv2 rounded-[16px] h-[58px] absolute top-0 left-0 right-0 z-0 pointer-events-none"
          style={{
            filter: 'blur(2.5px)'
          }}
        />
        <div className="bg-background-elevated rounded-[16px] py-[10px] pl-[18px] pr-[10px] absolute top-0 left-0 right-0 flex items-center justify-between h-[58px] z-10">
          <div className="flex flex-col gap-[1px]">
            <p className="text-[14px] font-semibold leading-[17px] text-text-main-lv2">
              {t('BettieChatRoom.letterOpenBanner.title', {
                nickname: nickname
              })}
            </p>
            <p className="text-[13px] leading-[16px] text-text-main-lv2">
              {t('BettieChatRoom.letterOpenBanner.description')}
            </p>
          </div>
          <div className="relative">
            <div
              className="absolute inset-0 rounded-[40px] pointer-events-none bg-gradient-multi-rainbox-lv2"
              style={{
                filter: 'blur(2.5px)'
              }}
            />
            <button
              type="button"
              className="relative rounded-[40px] bg-[linear-gradient(92deg,rgba(255,255,255,0.50)_31.85%,rgba(255,255,255,0.10)_102.92%)] border border-[rgba(255,255,255,0.60)] px-[27px] py-[11px] text-[13px] font-medium leading-[16px] text-text-main-lv2"
              onClick={() => navigate(`/bettie/letter/${fanLetterId}`)}
            >
              {t('BettieChatRoom.letterOpenBanner.button')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// const SubscriptionUpgradeBanner = () => {
//   return (
//     <div className="fixed top-[calc(env(safe-area-inset-top)+64px)] left-0 right-0 flex items-center mx-[8px] justify-between bg-background-elevated py-[9px] pr-[15px] pl-[14px] rounded-[12px] shadow-[0_0_4px_0_rgba(0,0,0,0.05),_0_0_11.6px_0_rgba(0,0,0,0.05)] z-[51]">
//       <div className="flex items-center gap-[10px]">
//         <img src="/assets/bettie/bettie_upgrade_icon.png" alt="" className="w-[30px] h-[26px]" />
//         <div className="flex items-center gap-[4px]">
//           <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3 whitespace-pre-line">
//             {t('BettieChatRoom.upgradeSubscription')}
//           </p>
//           <ArrowRightSmall size={18} className="text-text-main-lv3" />
//         </div>
//       </div>
//       <Xmark size={14} className="text-text-main-lv3" />
//     </div>
//   );
// };
